import React, { useRef, useState } from 'react';

import { ApPopover } from '@alixpartners/ui-components';
import {
  ClassificationModalEditor,
  ITaxonomyItem,
  IRuleItem,
  SpendTypeEnum,
} from 'components/Classification';
import { DeleteConfirmationModal } from '../DeleteConfirmationModal';
import {
  Cell,
  PopoverContent,
  StyledButtonIcon,
} from './RuleCellRenderer.styles';

interface IProps {
  editRule(data: IRuleItem): Promise<any>;
  deleteRule(data: IRuleItem): Promise<any>;
  spendType: SpendTypeEnum;
  value?: number | string | null;
  taxonomyOptions: ITaxonomyItem[];
  columnGroups: string[];
  data: any;
  hasWritePermission: boolean;
}

export const RuleCellRenderer: React.FC<IProps> = ({
  editRule,
  columnGroups,
  deleteRule,
  spendType,
  taxonomyOptions,
  value,
  data,
  hasWritePermission,
}: any) => {
  const ref = useRef<any>(null);
  const [isTooltipOpen, setTooltipOpen] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [isEditorOpen, setEditorOpen] = useState(false);
  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const toggleOpen = () => setTooltipOpen(!isTooltipOpen);

  const showEditor = () => {
    setTooltipOpen(false);
    setEditorOpen(true);
  };
  const hideEditor = () => setEditorOpen(false);
  const showDeleteConfirm = () => {
    setTooltipOpen(false);
    setDeleteConfirmationOpen(true);
  };
  const hideDeleteConfirm = () => setDeleteConfirmationOpen(false);

  const handlerDeleteRule = () => {
    setSaving(true);

    deleteRule({ ruleId: data.ruleId }).then(() => {
      setSaving(false);
      hideEditor();
    });
  };

  const handleEditorSubmit = (editorData: ITaxonomyItem) => {
    const updateProps = {
      apSpendCategoryL1: editorData.apL1Category,
      apSpendCategoryL2: editorData.apL2Category,
      categoryL1Code: editorData.l1Code,
      categoryL2Code: editorData.l2Code,
      ruleId: data.ruleId,
    };
    setSaving(true);

    editRule(updateProps).then(() => {
      setSaving(false);
      hideEditor();
    });
  };
  const editorValue = {
    apL1Category: data.apSpendCategoryL1,
    apL2Category: data.apSpendCategoryL2,
  };

  const getGroupValues = () => {
    return columnGroups.map((key: string) => data[key]).join(' / ');
  };

  return (
    <React.Fragment>
      <Cell ref={ref} onClick={hasWritePermission ? toggleOpen : undefined}>
        {value}
      </Cell>
      {isTooltipOpen && (
        <ApPopover
          referenceElement={ref.current}
          onClose={toggleOpen}
          placement="top"
        >
          <PopoverContent>
            <StyledButtonIcon iconName="outline_edit" onClick={showEditor} />
            <StyledButtonIcon
              iconName="delete_outline"
              onClick={showDeleteConfirm}
            />
          </PopoverContent>
        </ApPopover>
      )}

      {isEditorOpen && (
        <ClassificationModalEditor
          isOpen={isEditorOpen}
          onClose={hideEditor}
          onSubmit={handleEditorSubmit}
          options={taxonomyOptions}
          isDisabled={isSaving}
          spendType={spendType}
          value={editorValue}
          isRule
        />
      )}
      {isDeleteConfirmationOpen && (
        <DeleteConfirmationModal
          isOpen={isDeleteConfirmationOpen}
          isDisabled={isSaving}
          onDelete={handlerDeleteRule}
          onClose={hideDeleteConfirm}
          value={getGroupValues()}
        />
      )}
    </React.Fragment>
  );
};
